import { useMemo } from 'react'
import type { Activity, ActivityDetails } from '../../../../../typings'
import { ActivityStatus } from '@user/features/Activity/constants'
import useTheme from '@mui/material/styles/useTheme'
import { ActivitiesHelper } from '@user/features/Activity/helpers'
import { useLocalizationDateFormat } from '@user/features/Localization/useLocalizationDateFormat'
import { timeFormat } from '@sales-center/common'

type Props = {
  activity: Activity
}

export function useData(props: Props) {
  const { activity } = props
  const theme = useTheme()
  const { formatDate } = useLocalizationDateFormat()

  const dateColor = useMemo(
    () =>
      ActivitiesHelper.isOverdueActivity(activity as ActivityDetails)
        ? 'error'
        : theme.palette.text.primary,
    [activity, theme.palette.text.primary],
  )

  const activityInfo = useMemo(() => {
    const date =
      activity.status === ActivityStatus.Complete
        ? activity.completedDate
        : activity.dueDate
    return {
      subject: activity.subject,
      date: formatDate(date) + ' - ' + timeFormat(date),
      status: (activity.status === (ActivityStatus.InProgress as string)
        ? 'In Progress'
        : activity.status === ActivityStatus.Todo
        ? 'to do'
        : activity.status === ActivityStatus.Complete
        ? 'Completed'
        : activity.status
      ).toUpperCase(),
    }
  }, [activity])

  const color =
    activity.status === ActivityStatus.Todo
      ? '#E3F2FF'
      : activity.status === ActivityStatus.Deferred
      ? '#FFF0D4'
      : activity.status === ActivityStatus.Complete
      ? 'rgba(102, 187, 106, 0.08)'
      : 'rgba(106, 99, 147, 0.1)'

  const tagTextColor =
    activity.status === ActivityStatus.Complete
      ? theme.palette.text.secondary
      : activity.status === ActivityStatus.Deferred
      ? '#551F00'
      : '#251A63'

  const showDate = activity.status !== ActivityStatus.Deferred
  const isCompleted = activity.status === ActivityStatus.Complete

  return {
    activityInfo,
    dateColor,
    color,
    showDate,
    isCompleted,
    tagTextColor,
  }
}
